import { useCallback, useMemo, useState } from 'react';
import { subYears } from 'date-fns';

import { DataStoreType, StatementsFilters } from '../store';
import {
  dateRangesUTC,
  DateRangeUTCId,
  endOfYearUTC,
  startOfYearUTC,
} from '../utils';
import { Enriched } from '../types';

export const useStatementsFilters = (
  DataStore: DataStoreType,
  onSubmit?: (filters?: StatementsFilters) => void,
  onReset?: () => void,
  initialFilters?: Partial<StatementsFilters>
) => {
  const accounts = DataStore.useStoreState(s => s.portfolio.accounts);
  const filters = DataStore.useStoreState(s => s.statements.filters);
  const findAccount = DataStore.useStoreState(s => s.portfolio.findAccount);

  const { getList, setFilters } = DataStore.useStoreActions(a => ({
    getList: a.statements.getList,
    setFilters: a.statements.setFilters,
  }));

  // State
  const [accountId, setAccountId] = useState<string | null>(
    (initialFilters?.accountId || filters?.accountId) ?? null
  );
  const [startDate, setStartDate] = useState<Date | null>(
    (initialFilters?.startDate || filters?.startDate) ?? null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    (initialFilters?.endDate || filters?.endDate) ?? null
  );
  const [dateRangeId, setDateRangeId] = useState<DateRangeUTCId | null>(
    (initialFilters?.dateRangeId || filters?.dateRangeId) ?? null
  );

  // Vars
  const dateRangeValues = useMemo(() => {
    const options = [...dateRangesUTC];

    const now = new Date();
    const currentYear = now.getFullYear(); // User TZ

    // Now is: 2025

    // 2024
    options.push({
      id: currentYear - 1,
      label: `${currentYear - 1}`,
      startDate: startOfYearUTC(subYears(now, 1)),
      endDate: endOfYearUTC(subYears(now, 1)),
    });

    // 2023
    options.push({
      id: currentYear - 2,
      label: `${currentYear - 2}`,
      startDate: startOfYearUTC(subYears(now, 2)),
      endDate: endOfYearUTC(subYears(now, 2)),
    });

    return options;
  }, []);

  const account = useMemo(() => {
    return accounts.find(_account => _account.account?.accountId === accountId);
  }, [accountId, accounts]);

  // Handlers
  const handleChangeAccount = useCallback(
    (account: Enriched.ListAccountItem | string) => {
      if (typeof account === 'string') {
        setAccountId(
          findAccount(accounts, account)?.account?.accountId ?? null
        );
      } else {
        setAccountId(account.account?.accountId ?? null);
      }
    },
    [accounts, findAccount]
  );

  const handleChangeDateRange = useCallback(
    (id: DateRangeUTCId) => {
      const value = dateRangeValues.find(_dateRange => _dateRange.id === id);

      if (value) {
        if (value.id === dateRangeId) {
          setDateRangeId(null);
          setStartDate(null);
          setEndDate(null);
        } else {
          setDateRangeId(value.id);
          setStartDate(value.startDate);
          setEndDate(value.endDate);
        }
      }
    },
    [dateRangeId, dateRangeValues]
  );

  const handleReset = useCallback(() => {
    setAccountId(null);
    setStartDate(null);
    setEndDate(null);
    setDateRangeId(null);

    setFilters(null);

    getList({
      limit: null,
      startDate: null,
      endDate: null,
      accountId: null,
      assetCode: null,
      page: null,
      accountTypes: null,
    });
    onReset?.();
  }, [getList, setFilters, onReset]);

  const handleSubmit = useCallback(() => {
    const newFilters: StatementsFilters = {
      ...filters,
      dateRangeId: dateRangeId,
      startDate,
      endDate,
      accountId,
    };

    setFilters(newFilters);

    getList(newFilters);
    onSubmit?.(newFilters);
  }, [
    setFilters,
    dateRangeId,
    startDate,
    endDate,
    accountId,
    getList,
    onSubmit,
  ]);

  return {
    handleReset,
    handleSubmit,
    handleChangeDateRange,
    handleChangeAccount,
    accounts,
    account,
    accountId,
    startDate,
    endDate,
    dateRangeId,
    dateRangeValues,
  };
};
